import React, { useEffect, useMemo, useState } from "react";
import {
  TEAM_RESTORE_EMPTY,
  TEAM_RESTORE_MANAGER_CHOOSE,
  TEAM_RESTORE_MANAGER_KEEPS,
  TEAM_RESTORE_NAME_TAKEN,
  TEAM_RESTORE_NO_MANAGER,
  personLabel,
  restorePlan,
} from "../../lib/teamManagement";

/**
 * Възстановяване на екип.
 *
 * An archived team promised two things that may no longer be true: that its
 * Мениджър can lead it, and that its name is free. The dialog therefore asks for
 * exactly what changed and nothing else:
 *
 *   * the Мениджър is REUSED when the server still lists them as eligible, and the
 *     Owner is asked to choose one only when they are not. Nothing is preselected
 *     on their behalf;
 *   * the name is editable, because only ACTIVE names are unique and the old one
 *     may have been legitimately reused meanwhile.
 *
 * The team comes back EMPTY: its Търговци are not re-added and its purged
 * invitations are not revived. The server re-validates all of it.
 */
export const TeamRestoreDialog = ({
  open,
  team,
  teams,
  managers,
  submitting,
  error,
  onCancel,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [managerId, setManagerId] = useState("");

  const plan = useMemo(
    () => restorePlan(team, teams, managers),
    [team, teams, managers],
  );

  useEffect(() => {
    if (!open) return;
    setName(team?.name || "");
    // The historical Manager is preselected ONLY while they are still eligible;
    // otherwise the field starts empty and the Owner picks deliberately.
    setManagerId(plan.managerReusable ? team?.manager?.member_id || "" : "");
  }, [open, team?.id, team?.name, team?.manager?.member_id, plan.managerReusable]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  if (!open || !team) return null;

  const trimmed = name.trim();
  const ready = trimmed.length >= 2 && !!managerId;

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-restore-title"
        data-testid="team-restore-dialog"
      >
        <div className="h" id="team-restore-title">
          Възстановяване на екип
        </div>
        <div className="body">
          <div className="qw-field">
            <label htmlFor="team-restore-name">Име на екипа</label>
            <input
              id="team-restore-name"
              className="qw-input"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              data-testid="team-restore-name"
            />
            {plan.nameConflict && (
              <div className="qw-help" data-testid="team-restore-name-taken">
                {TEAM_RESTORE_NAME_TAKEN}
              </div>
            )}
          </div>

          <div className="qw-field" style={{ marginTop: 12 }}>
            <label htmlFor="team-restore-manager">Мениджър</label>
            <select
              id="team-restore-manager"
              className="qw-select"
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              disabled={submitting}
              data-testid="team-restore-manager"
            >
              <option value="">Изберете мениджър</option>
              {plan.managerOptions.map((m) => (
                <option
                  key={m.member_id}
                  value={m.member_id}
                  data-testid={`team-restore-manager-option-${m.member_id}`}
                >
                  {personLabel(m)}
                </option>
              ))}
            </select>
            {plan.managerReusable ? (
              <div className="qw-help" data-testid="team-restore-manager-keeps">
                {TEAM_RESTORE_MANAGER_KEEPS}
              </div>
            ) : (
              <div className="qw-help" data-testid="team-restore-manager-reason">
                {plan.managerReason} {TEAM_RESTORE_MANAGER_CHOOSE}
              </div>
            )}
            {plan.managerOptions.length === 0 && (
              <div className="qw-help" data-testid="team-restore-no-manager">
                {TEAM_RESTORE_NO_MANAGER}
              </div>
            )}
          </div>

          <div className="qw-help" style={{ marginTop: 10 }} data-testid="team-restore-note">
            {TEAM_RESTORE_EMPTY}
          </div>

          {error && (
            <div
              className="qw-error"
              role="alert"
              style={{ marginTop: 10 }}
              data-testid="team-restore-error"
            >
              {error}
            </div>
          )}
        </div>
        <div className="footer">
          <button
            type="button"
            className="qw-btn ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="team-restore-cancel"
          >
            Отказ
          </button>
          <button
            type="button"
            className="qw-btn primary"
            onClick={() => onSubmit({ name: trimmed, manager_member_id: managerId })}
            disabled={submitting || !ready}
            data-testid="team-restore-submit"
          >
            {submitting ? "Записване…" : "Възстанови"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamRestoreDialog;
